// client/src/api/auth.ts — the identity endpoints, typed by hand · AUTH-004 (04 §2.1 "token
// holder + login/logout/refresh orchestration").
//
// 04 §9.3 has these arrive generated from the OpenAPI contract. The contract does not exist yet
// — the API publishes no `openapi/openapi-v1.json`, DEVOPS-003 (the drift gate) has no branch —
// so the identity operations are transcribed here from `AuthEndpoints.cs`, `MfaEndpoints.cs`
// and `UsersEndpoints.cs`, request and response shapes alike: eleven then, twenty since the
// address confirmation added its two (D-2758, D-2759), twenty-two since the change of address
// added its own two (D-2981), twenty-five since the account deletion added its three (D-2990),
// twenty-six since „Deveniți proprietar" became a request and a mailed link (D-3308).
// The day the
// contract lands, `src/client/**` replaces this file and nothing above it changes: every
// caller already goes through `authApi.*` and never through `fetch`. See DEVIATIONS D-180.
//
// The token orchestration (single-flight refresh, session bootstrap) is NOT here — it is in
// `src/session/source.ts`, because it writes the session store and this module must not know
// the store exists.

import type { WireNumber } from '@/lib/wire';
import type { MfaMethod, OrganizationKind } from '@/session/types';

import { api } from './http';

const AUTH = '/api/v1/auth';
const USERS = '/api/v1/users';

/** One row of the organization switcher (`OrganizationSummary`). */
export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
  isDefault: boolean;
  /** A portfolio or a clădire's own organization (D-3343). Always sent. */
  kind: OrganizationKind;
}

/** `SessionResponse` — every call that issues a session answers with this. The refresh token is in a cookie, never here (02 §6.2). */
export interface SessionResponse {
  accessToken: string;
  expiresAtUtc: string;
  activeOrganizationId?: string | null;
  role: string;
  organizations: OrganizationSummary[];
  /**
   * The client this session acts for, or null when it acts for nobody (D-1694). The clients
   * themselves are on `/users/me`, not here: they change when an owner invites or revokes, not
   * when a token rotates.
   *
   * OPTIONAL, not `string | null`: the API omits a null member from the body entirely
   * (`JsonIgnoreCondition.WhenWritingNull`, 02 §7.4), so "no client" arrives as an ABSENT key
   * and reads back as `undefined`. Declaring it non-optional made every `=== null` test in the
   * SPA false for exactly the state it was written for (D-1708). `session/mapping.ts`
   * normalizes it; nothing else should have to.
   */
  activeClientId?: string | null;
}

/** `MfaChallengeResponse` — the second successful shape of `POST /auth/login` (02 §6.5). */
export interface MfaChallengeResponse {
  status: 'MFA_REQUIRED';
  expiresAtUtc: string;
  enrollmentRequired: boolean;
  methods: MfaMethod[];
}

export type LoginResponse = SessionResponse | MfaChallengeResponse;

/** The body says which of the two 200s this is; a client that read the status code would treat "not yet" as an error. */
export function isMfaChallenge(response: LoginResponse): response is MfaChallengeResponse {
  return 'status' in response && response.status === 'MFA_REQUIRED';
}

/**
 * `LoginRequest` — `POST /auth/login`. Exactly one of `email` and `userName` names the account
 * (D-2710): an address for everybody who signed up or was invited, a user name for the account a
 * landlord created for a tenant with no address. The server's validator refuses both and
 * neither alike.
 *
 * `newPassword` travels only on the SECOND attempt of a first sign-in with a one-time password
 * (D-2712): the first is answered 403 `password-change-required`, and the same user name and
 * one-time password are sent again with the password the person chose. Optional rather than
 * `string | null`, so a plain sign-in carries exactly the body it always did.
 */
export interface LoginRequest {
  email: string | null;
  password: string;
  userName?: string;
  newPassword?: string;
}

/** `CurrentUserResponse` — `GET /users/me`, what the token deliberately does not carry. */
export interface CurrentUserResponse {
  userId: string;
  /**
   * The address the account signs in with — ABSENT for an account a landlord created for a
   * tenant with no address, which signs in with `userName` instead (D-2710). One of the two is
   * always present. Optional, not `string | null`, for the reason `SessionResponse` gives: the
   * API omits a null member.
   */
  email?: string | null;
  /** The login of an account without an address (D-2710); absent for everybody else. */
  userName?: string | null;
  fullName: string;
  /** The contact number, or absent when the person never gave one (D-2032). */
  phone?: string | null;
  mfaEnabled: boolean;
  activeOrganizationId?: string | null;
  role: string;
  permissions: string[];
  organizations: OrganizationSummary[];
  /**
   * How many properties the account holds a current quota in, across every organization it
   * belongs to (D-1625). Typed as the contract types every numeric member — see `lib/wire`.
   */
  ownedPropertyCount: WireNumber;
  /** Every client this account may work for (D-1699); empty for somebody acting as themselves. */
  clients: ClientSummary[];
  /** Which of them is chosen. Absent — not null — when none is; see `SessionResponse` (D-1708). */
  activeClientId?: string | null;
  /**
   * How many contracts the account is a leasing PARTY to — the leases it rents rather than the
   * ones it lets — across every organization it belongs to (D-1727). It is the one member of
   * this response that is not about the active organization.
   */
  portalContractCount: WireNumber;
  /**
   * How many clădiri the account lives in — its ACTIVE residencies, counted by clădire, across every
   * organization it belongs to (BR-BLD-08, D-3368). Like the lease count above, not about the active
   * organization: the portal's „Clădire" tab is shown on it.
   */
  residentBuildingCount: WireNumber;
  /**
   * When the deletion the person confirmed runs (D-2990, D-2995) — what the banner on both shells
   * reads. ABSENT, not null, when none is scheduled: the API omits a null member (D-1708).
   */
  deletionScheduledForUtc?: string | null;
}

/** `ClientSummary` — one row of „Lucrez pentru". */
export interface ClientSummary {
  /** The delegation — the argument to `switchClient`. */
  id: string;
  principalName: string;
  organizationId: string;
  organizationName: string;
  role: string;
}

/** `TotpEnrollmentResponse` — shown once; the SPA renders the URI as a QR and offers the secret for typing. */
export interface TotpEnrollmentResponse {
  secret: string;
  provisioningUri: string;
}

/** `MfaEnrollmentResponse` — the ten codes, plus a session when confirming finished a blocked login. */
export interface MfaEnrollmentResponse {
  recoveryCodes: string[];
  session: SessionResponse | null;
}

export interface RecoveryCodesResponse {
  recoveryCodes: string[];
}

/**
 * `RegisterRequest` (05 AUTH-001): user + first organization + `owner` membership in one call.
 * The person only, since D-1530 — the organization is named after them and its slug derived
 * server-side, so there is nothing about it to send.
 */
export interface RegisterRequest {
  email: string;
  password: string;
  fullName: string;
  phone: string | null;

  /**
   * Where the person goes after their first sign-in — the invitation or hand-over page the
   * signup started from. The server carries it in the confirmation link as `?next=` and drops
   * anything that is not a path of this application (D-2753); the SPA sends it only when
   * `safeRedirect` would follow it (D-2762).
   */
  returnTo?: string | null;
}

/**
 * `RegistrationPendingResponse` — what a signup answers since D-2750: 202, no session, no
 * cookie. The account exists and signs in once the address is confirmed from the mailed link.
 */
export interface RegistrationPendingResponse {
  /** Where the confirmation link was mailed. */
  email: string;

  /**
   * False when the recipient's allowance refused the send (D-2652) or the transport did not
   * accept it. The account is created either way; the panel says so and offers the resend.
   */
  verificationSent: boolean;
}

/** `VerifiedEmailResponse` — a redeemed confirmation link: the address that is now confirmed (D-2752). */
export interface VerifiedEmailResponse {
  email: string;
}

/**
 * `EmailChangePendingResponse` — a change of address asked for (D-2975): 202, and nothing has
 * changed yet. The address moves when the link mailed to `email` is opened.
 */
export interface EmailChangePendingResponse {
  /** The new address, as the server will store it: trimmed and lower-cased. */
  email: string;

  /**
   * False when the mail carrying the link was not accepted for delivery. The address does not
   * change either way until a link is opened, so the screen says to try again.
   */
  confirmationSent: boolean;
}

/** `ChangedEmailResponse` — a change link redeemed (D-2977): the address the account now signs in with. */
export interface ChangedEmailResponse {
  email: string;
}

/**
 * `AccountDeletionPendingResponse` — a deletion asked for (D-2990): 202, and nothing is scheduled
 * yet. The deletion is scheduled when the link mailed to the account's own address is opened.
 */
export interface AccountDeletionPendingResponse {
  /** False when the mail carrying the link was not accepted for delivery; the screen says to try again. */
  confirmationSent: boolean;
}

/**
 * `AccountDeletionScheduledResponse` — a deletion link redeemed (D-2990): the account, and when it
 * is deleted. Until then it signs in as usual and can cancel.
 */
export interface AccountDeletionScheduledResponse {
  email: string;
  scheduledForUtc: string;
}

/**
 * `BecomeOwnerPendingResponse` — „Deveniți proprietar" asked for (D-3308): 202, and nothing has
 * changed yet. The organization is created when the link mailed to `email` is opened.
 */
export interface BecomeOwnerPendingResponse {
  /** The account's own address, where the link went. */
  email: string;

  /** False when the mail carrying the link was not accepted for delivery; the screen says to try again. */
  confirmationSent: boolean;
}

export const authApi = {
  /** An address or a user name, and on a first sign-in the chosen password too (D-2710, D-2712). */
  login: (request: LoginRequest) =>
    api<LoginResponse>(`${AUTH}/login`, { method: 'POST', body: request }),

  /**
   * 202 and no session since D-2750: the first sign-in waits for the address to be confirmed,
   * so the signup ends on the panel that says where the link went (D-2758).
   */
  register: (request: RegisterRequest) =>
    api<RegistrationPendingResponse>(`${AUTH}/register`, { method: 'POST', body: request }),

  /**
   * The link from the confirmation mail (D-2752, D-2759). Idempotent — the same link opened
   * twice confirms twice — and it issues NO session: the person signs in afterwards with their
   * password, and every other rule of the login applies then.
   */
  verifyEmail: (token: string) =>
    api<VerifiedEmailResponse>(`${AUTH}/email/verify`, { method: 'POST', body: { token } }),

  /**
   * 202 whether or not the address has an account, is already confirmed or has been rationed
   * (D-2751) — the answer says nothing about which addresses exist, so neither may the screen.
   * An absent `returnTo` is left out of the body by `JSON.stringify`.
   */
  resendEmailVerification: (email: string, returnTo?: string) =>
    api<void>(`${AUTH}/email/resend`, { method: 'POST', body: { email, returnTo } }),

  /** The cookie carries the refresh token; there is no body to send. */
  refresh: () => api<SessionResponse>(`${AUTH}/refresh`, { method: 'POST' }),

  /** 204 whether or not a session existed — logging out is a request to end one. */
  logout: () => api<void>(`${AUTH}/logout`, { method: 'POST' }),

  /**
   * The link from the „Deveniți proprietar" mail (D-2033, D-3308). The refresh cookie says who is
   * asking, so it needs a session in this browser, and the token says that this account asked;
   * the organization is named after them server-side. The answer is a session of the
   * organization it has just created, so the caller feeds it to `establishSession` exactly as a
   * login does. 400 `become-owner-token-invalid` for a link that is forged, spent or past its 24
   * hours; 409 `already-owner` for an account that owns an organization already.
   */
  becomeOwner: (token: string) =>
    api<SessionResponse>(`${AUTH}/become-owner`, { method: 'POST', body: { token } }),

  switchOrganization: (organizationId: string) =>
    api<SessionResponse>(`${AUTH}/switch-organization`, { method: 'POST', body: { organizationId } }),

  /**
   * „Lucrez pentru" (D-1694). A sibling of the organization switch in every respect: the
   * refresh cookie authorizes it, the pair rotates, and the new access token carries the
   * client's `role` and `org_id` as well as its `client_id`.
   */
  switchClient: (delegationId: string) =>
    api<SessionResponse>(`${AUTH}/switch-client`, { method: 'POST', body: { delegationId } }),

  /** 202 whether or not the address exists (02 §6.1). */
  forgotPassword: (email: string) =>
    api<void>(`${AUTH}/password/forgot`, { method: 'POST', body: { email } }),

  resetPassword: (token: string, newPassword: string) =>
    api<void>(`${AUTH}/password/reset`, { method: 'POST', body: { token, newPassword } }),

  mfa: {
    /** Bearer token OR the pending cookie — the endpoint takes the caller as it finds them. */
    enroll: () => api<TotpEnrollmentResponse>(`${AUTH}/mfa/totp/enroll`, { method: 'POST' }),

    confirm: (code: string) =>
      api<MfaEnrollmentResponse>(`${AUTH}/mfa/totp/confirm`, { method: 'POST', body: { code } }),

    verify: (method: MfaMethod, code: string) =>
      api<SessionResponse>(`${AUTH}/mfa/verify`, { method: 'POST', body: { method, code } }),

    sendEmailCode: () => api<void>(`${AUTH}/mfa/email/send`, { method: 'POST' }),

    /** Authenticated AND a live code — the previous ten stop working when these are written. */
    regenerateRecoveryCodes: (code: string) =>
      api<RecoveryCodesResponse>(`${AUTH}/mfa/recovery-codes`, { method: 'POST', body: { code } }),

    /** Authenticated AND a live code; every refresh family is revoked server-side. */
    disable: (code: string) => api<void>(`${AUTH}/mfa/disable`, { method: 'POST', body: { code } }),
  },

  me: () => api<CurrentUserResponse>(`${USERS}/me`),

  /**
   * „Profilul meu" (D-2031). PUT and not PATCH: the body is the whole of what a person may
   * edit about themselves, so an omitted phone means „no phone" rather than „leave it alone".
   * 204 — the session is refreshed from `/users/me`, which stays the one source for it.
   */
  updateProfile: (fullName: string, phone: string | null) =>
    api<void>(`${USERS}/me`, { method: 'PUT', body: { fullName, phone } }),

  /** Every refresh family is revoked server-side; the caller is logged out by the response. */
  changePassword: (currentPassword: string, newPassword: string) =>
    api<void>(`${USERS}/me/password`, { method: 'POST', body: { currentPassword, newPassword } }),

  /**
   * „Adresa de e-mail" (D-2975). Re-proof in the body, not step-up (R3): the current password,
   * and a live code when the account has two-step sign-in. 202 — nothing changes until the link
   * mailed to the new address is opened. An absent `totpCode` is left out by `JSON.stringify`.
   */
  requestEmailChange: (newEmail: string, currentPassword: string, totpCode?: string) =>
    api<EmailChangePendingResponse>(`${USERS}/me/email`, {
      method: 'POST',
      body: { newEmail, currentPassword, totpCode },
    }),

  /**
   * The link from the change mail (D-2977, D-2981). Anonymous and idempotent — the same link
   * opened twice answers the same address — and it neither issues nor ends a session.
   */
  confirmEmailChange: (token: string) =>
    api<ChangedEmailResponse>(`${AUTH}/email/confirm-change`, { method: 'POST', body: { token } }),

  /**
   * „Ștergerea contului" (D-2990). Re-proof in the body, as for the change of address (R3,
   * D-2991): the current password, and a live code when the account has two-step sign-in. 202 —
   * nothing is scheduled until the link mailed to the account's own address is opened. Refused
   * 422 for a platform administrator, an account without an address and the only owner of an
   * organization that still holds properties or active contracts (D-2993); 409 while a deletion
   * is already scheduled.
   */
  requestAccountDeletion: (currentPassword: string, totpCode?: string) =>
    api<AccountDeletionPendingResponse>(`${USERS}/me/deletion`, {
      method: 'POST',
      body: { currentPassword, totpCode },
    }),

  /**
   * „Deveniți proprietar" from the portal's settings (D-3308). No body: the bearer says who is
   * asking and the link goes to the account's own address. 202 — nothing changes until the link
   * is opened. Refused 409 `already-owner`, 422 `become-owner-no-address` for an account with no
   * address to mail (D-2710), 429 by the recipient's allowance (D-2652) or 422
   * `email-recipient-suppressed` (D-3053), as the deletion request is, and 401
   * `invalid-credentials` for an account the server no longer knows.
   */
  requestBecomeOwner: () =>
    api<BecomeOwnerPendingResponse>(`${USERS}/me/become-owner`, { method: 'POST' }),

  /**
   * „Anulați ștergerea" (D-2990): 204, and the person is told by mail. 409
   * `account-deletion-not-pending` when nothing is scheduled.
   */
  cancelAccountDeletion: () => api<void>(`${USERS}/me/deletion`, { method: 'DELETE' }),

  /**
   * The link from the deletion mail (D-2990, D-2992). Anonymous; it schedules the deletion
   * fourteen days out and answers when. Every failure is 400 `account-deletion-token-invalid`, and
   * the same link opened again while the deletion waits answers the same 200.
   */
  confirmAccountDeletion: (token: string) =>
    api<AccountDeletionScheduledResponse>(`${AUTH}/account-deletion/confirm`, {
      method: 'POST',
      body: { token },
    }),
};
